import React, { useMemo } from 'react';
import { calcularHosTotales } from '../../data/higieneOralSimplificada';
import { hosTieneDatos } from '../../utils/mspReadHelpers';
import MspReadEmpty from './MspReadEmpty';
import MspSelectionBox from './MspSelectionBox';

const OPCIONES_VACIO = [{ value: '', label: '—' }];

function opcionesRango(max) {
  return [
    ...OPCIONES_VACIO,
    ...Array.from({ length: max + 1 }, (_, i) => ({ value: String(i), label: String(i) })),
  ];
}

const OPS_PLACA = opcionesRango(3);
const OPS_CALCULO = opcionesRango(3);
const OPS_GINGIVITIS = opcionesRango(1);

function scorePillClass(kind, value) {
  if (value === '' || value == null) return 'hos-score-pill hos-score-pill--empty';
  const n = Number(value);
  if (kind === 'gingivitis') {
    return n >= 1 ? 'hos-score-pill hos-score-pill--warn' : 'hos-score-pill hos-score-pill--ok';
  }
  if (n >= 3) return 'hos-score-pill hos-score-pill--high';
  if (n >= 2) return 'hos-score-pill hos-score-pill--mid';
  if (n >= 1) return 'hos-score-pill hos-score-pill--low';
  return 'hos-score-pill hos-score-pill--ok';
}

function ScoreSelect({ value, options, onChange, readOnly, ariaLabel, scoreKind }) {
  if (readOnly) {
    const v = value !== '' && value != null ? value : '—';
    return <span className={scorePillClass(scoreKind, value)}>{v}</span>;
  }
  return (
    <select
      className="hos-score-select"
      value={value ?? ''}
      onChange={(e) => onChange(e.target.value)}
      aria-label={ariaLabel}
    >
      {options.map((o) => (
        <option key={o.value} value={o.value}>{o.label}</option>
      ))}
    </select>
  );
}

function MarcaCell({ value, onToggle, readOnly, pieza }) {
  const marked = Boolean(value);
  return (
    <td className={`hos-cell hos-cell--marca${marked ? ' hos-cell--marca-on' : ''}`}>
      <MspSelectionBox
        variant="cell"
        selected={marked}
        readOnly={readOnly}
        onToggle={onToggle}
        ariaLabel={marked ? `Quitar evaluación pieza ${pieza}` : `Marcar pieza ${pieza} evaluada`}
      />
    </td>
  );
}

/**
 * Tabla MSP «Higiene oral simplificada» (6 filas × placa/cálculo/gingivitis + totales).
 */
export default function HigieneOralSimplificadaTable({
  value,
  onChange,
  readOnly = false,
  showSectionTitle = true,
}) {
  const filas = value?.filas || [];
  const totales = useMemo(() => calcularHosTotales(filas), [filas]);

  const updateFila = (index, patch) => {
    if (!onChange) return;
    const next = filas.map((f, i) => (i === index ? { ...f, ...patch } : f));
    onChange({ filas: next });
  };

  const toggleMarca = (filaIdx, marcaIdx) => {
    const fila = filas[filaIdx];
    if (!fila) return;
    const marcas = [...(fila.marcas || ['', '', ''])];
    marcas[marcaIdx] = marcas[marcaIdx] ? '' : '1';
    updateFila(filaIdx, { marcas });
  };

  const hasHosData = hosTieneDatos({ higieneOralSimplificada: { filas } });

  if (readOnly && !hasHosData) {
    return (
      <MspReadEmpty
        icon="bi-droplet-half"
        message="Higiene oral simplificada (H.O.S.) sin calificar. Complétela en la ficha odontológica."
      />
    );
  }

  return (
    <div className={`hos-panel${readOnly ? ' hos-panel--readonly' : ' hos-panel--edit'}`}>
      {showSectionTitle && (
        <div className="hos-panel__titles">
          <div className="hos-panel__header-main">
            <i className="bi bi-droplet-half" aria-hidden />
            7 Indicadores de salud bucal
          </div>
          <div className="hos-panel__header-sub">Higiene oral simplificada (H.O.S.)</div>
        </div>
      )}

      {!readOnly && (
        <div className="hos-panel__guide" role="note">
          <span><strong>×</strong> = pieza evaluada</span>
          <span>Placa / Cálculo: <strong>0–3</strong></span>
          <span>Gingivitis: <strong>0–1</strong></span>
        </div>
      )}

      <div className="hos-table-scroll">
        <table className="hos-table" role="grid" aria-label="Higiene oral simplificada">
          <thead>
            <tr>
              <th className="hos-th hos-th--rownum" scope="col" aria-label="Fila">
                #
              </th>
              <th colSpan={6} className="hos-th hos-th--piezas">
                Piezas dentales
              </th>
              <th className="hos-th hos-th--score hos-th--score-placa">
                Placa
                <span className="hos-th-range">0 · 1 · 2 · 3</span>
              </th>
              <th className="hos-th hos-th--score hos-th--score-calculo">
                Cálculo
                <span className="hos-th-range">0 · 1 · 2 · 3</span>
              </th>
              <th className="hos-th hos-th--score hos-th--score-gingivitis">
                Gingivitis
                <span className="hos-th-range">0 · 1</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {filas.map((fila, rowIdx) => (
              <tr key={`hos-row-${rowIdx}`}>
                <td className="hos-cell hos-cell--rownum">{rowIdx + 1}</td>
                {(fila.piezas || [null, null, null]).map((pieza, colIdx) => (
                  <React.Fragment key={`${rowIdx}-${colIdx}`}>
                    <td className="hos-cell hos-cell--pieza">{pieza ?? '—'}</td>
                    <MarcaCell
                      value={(fila.marcas || [])[colIdx]}
                      readOnly={readOnly}
                      pieza={pieza}
                      onToggle={() => toggleMarca(rowIdx, colIdx)}
                    />
                  </React.Fragment>
                ))}
                <td className="hos-cell hos-cell--input">
                  <ScoreSelect
                    value={fila.placa}
                    options={OPS_PLACA}
                    readOnly={readOnly}
                    ariaLabel={`Placa fila ${rowIdx + 1}`}
                    onChange={(v) => updateFila(rowIdx, { placa: v })}
                  />
                </td>
                <td className="hos-cell hos-cell--input">
                  <ScoreSelect
                    value={fila.calculo}
                    options={OPS_CALCULO}
                    readOnly={readOnly}
                    ariaLabel={`Cálculo fila ${rowIdx + 1}`}
                    onChange={(v) => updateFila(rowIdx, { calculo: v })}
                  />
                </td>
                <td className="hos-cell hos-cell--input">
                  <ScoreSelect
                    value={fila.gingivitis}
                    options={OPS_GINGIVITIS}
                    readOnly={readOnly}
                    ariaLabel={`Gingivitis fila ${rowIdx + 1}`}
                    onChange={(v) => updateFila(rowIdx, { gingivitis: v })}
                  />
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="hos-foot-totales">
              <td colSpan={7} className="hos-cell hos-cell--totales-label">
                <span className="hos-totales-title">Totales</span>
              </td>
              {[
                { key: 'placa', value: totales.placa, kind: 'placa' },
                { key: 'calculo', value: totales.calculo, kind: 'calculo' },
                { key: 'gingivitis', value: totales.gingivitis, kind: 'gingivitis' },
              ].map(({ key, value, kind }) => {
                const filled = value !== '' && value != null;
                return (
                  <td key={key} className={`hos-cell hos-cell--total hos-cell--total-${kind}`}>
                    <span
                      className={`msp-total-val${filled ? ' msp-total-val--filled' : ' msp-total-val--empty'}`}
                      title={filled ? `Total ${kind}` : 'Sin datos — marque piezas y puntuaciones'}
                    >
                      {filled ? value : '—'}
                    </span>
                  </td>
                );
              })}
            </tr>
          </tfoot>
        </table>
      </div>

      {(totales.promedioPlaca !== '' || totales.promedioCalculo !== '' || totales.promedioGingivitis !== '') && (
        <p className="hos-promedios small text-muted mb-0 mt-2">
          Promedio índice:&nbsp;
          {totales.promedioPlaca !== '' && <span>Placa <strong>{totales.promedioPlaca}</strong></span>}
          {totales.promedioCalculo !== '' && (
            <span className="ms-2">Cálculo <strong>{totales.promedioCalculo}</strong></span>
          )}
          {totales.promedioGingivitis !== '' && (
            <span className="ms-2">Gingivitis <strong>{totales.promedioGingivitis}</strong></span>
          )}
        </p>
      )}

      {!readOnly && (
        <p className="hos-leyenda small text-muted mb-0 mt-2">
          Marque las piezas evaluadas con × en el cuadro junto a cada número. Placa y cálculo: 0 a 3; gingivitis: 0 a 1.
        </p>
      )}
    </div>
  );
}
